import { lazy } from "nodash"
import type {
    AnyTask,
    BrowserWorkerHandle,
    Conforms,
    PoolContract,
    WorkerInbound,
    WorkerOutbound,
} from "./shared"
import { createPool, type PoolWorker } from "./src/pool"
import { startWorkerRuntime } from "./src/worker-runtime"

export { defineTask } from "./src/task"

let createWorkerHandle: (() => BrowserWorkerHandle) | undefined

const wrapWorker = (handle: BrowserWorkerHandle): PoolWorker => ({
    postMessage: (message, transfer) => handle.postMessage(message, transfer),
    onMessage: (listener) => {
        const onEvent = (event: { data: unknown }) => listener(event.data as WorkerOutbound)
        handle.addEventListener("message", onEvent)
        return () => handle.removeEventListener("message", onEvent)
    },
    terminate: () => handle.terminate(),
})

const getPool = lazy(() =>
    createPool(
        {
            createWorker: () => {
                const handle = createWorkerHandle?.()
                if (!handle) {
                    throw new Error("webworker-pool: expected a browser Worker")
                }
                return wrapWorker(handle)
            },
            loadWasmBinary: (fileName) =>
                fetch(`/${fileName}`).then((response) => response.arrayBuffer()),
        },
        getNumberOfWorkers()
    )
)

export const installWorkerPool = ({
    createWorker,
}: {
    createWorker: () => BrowserWorkerHandle
}) => {
    createWorkerHandle = createWorker
}

export const runTask: PoolContract["runTask"] = (task, args, options) =>
    getPool().runTask(task, args, options)

export const getNumberOfWorkers = () => Math.max(1, (navigator.hardwareConcurrency || 4) - 1)

export const cleanupWorkers = () => getPool().terminate()

export const startWorker = (tasks: readonly AnyTask[]) =>
    startWorkerRuntime(
        {
            postMessage: (message, transfer) => self.postMessage(message, { transfer }),
            onMessage: (listener) =>
                self.addEventListener("message", (event: MessageEvent<WorkerInbound>) =>
                    listener(event.data)
                ),
        },
        tasks
    )

type _Conforms = Conforms<typeof import("./browser")>
